import { useAuth, useUser } from "@tellescope/tanstack-query";
import { Button, ExpandableDialogWithSidebar } from "@tellescope/ui";
import { formatName } from "@tellescope/utils";
import { BadgeCheck, LayoutDashboard, LogOut } from "lucide-react";
import { Dispatch, SetStateAction, useState } from "react";
import { useTranslation } from "react-i18next";
import { AccountSection, DashboardSection } from "./sections";

export interface AccountDialogProps {
  userId?: string;
  open?: boolean;
  setOpen?: Dispatch<SetStateAction<boolean>>;
}

export const AccountDialog = ({ userId: providedUserId, open, setOpen }: AccountDialogProps) => {
  const { t } = useTranslation("auth");
  const { currentUser, logout } = useAuth();
  const userId = providedUserId || currentUser.id;
  const [selectedSection, setSelectedSection] = useState("Account");

  const user = useUser(userId ?? "");

  if (!userId || !user) return null;

  const userName = formatName(user);

  return (
    <ExpandableDialogWithSidebar
      title={userName || t("Account Settings")}
      selectedSection={selectedSection}
      setSelectedSection={setSelectedSection}
      open={open}
      setOpen={setOpen}
    >
      <ExpandableDialogWithSidebar.Section sectionKey="Account" title={t("Account")} icon={BadgeCheck}>
        <AccountSection userId={userId} />
      </ExpandableDialogWithSidebar.Section>
      <ExpandableDialogWithSidebar.Section sectionKey="Dashboard" title={t("Dashboard")} icon={LayoutDashboard}>
        <DashboardSection userId={userId} />
      </ExpandableDialogWithSidebar.Section>
      <ExpandableDialogWithSidebar.Section
        sectionKey="Logout"
        title={t("Logout")}
        customSidebarComponent={
          <Button
            variant="ghost"
            size="sm"
            className="flex items-center text-red-500 px-2 py-1 hover:bg-red-100 hover:text-red-700 rounded-sm transition-all w-full justify-start"
            onClick={() => logout()}
          >
            <LogOut size={16} />
            {t("Logout")}
          </Button>
        }
      />
    </ExpandableDialogWithSidebar>
  );
};
